import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, X, ArrowUp, ArrowDown, Tags, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CAT_SCOPE = '__category__';

export const CategoryBrandsSection = ({ formData, setFormData, token }) => {
    const [scope, setScope] = useState(CAT_SCOPE);
    const [allBrands, setAllBrands] = useState([]);
    const [addBrandId, setAddBrandId] = useState('');
    const [newBrandName, setNewBrandName] = useState('');
    const [creating, setCreating] = useState(false);

    const subs = formData.subcategories || [];
    const subSettings = formData.subcategory_brand_settings || {};
    const isCat = scope === CAT_SCOPE;
    const cfg = isCat ? formData : (subSettings[scope] || {});

    // Reset scope if the selected subcategory was removed/renamed
    useEffect(() => {
        if (!isCat && !subs.includes(scope)) setScope(CAT_SCOPE);
    }, [subs, scope, isCat]);

    const setCfg = (patch) => {
        if (isCat) {
            setFormData(prev => ({ ...prev, ...patch }));
        } else {
            setFormData(prev => ({
                ...prev,
                subcategory_brand_settings: {
                    ...(prev.subcategory_brand_settings || {}),
                    [scope]: { ...((prev.subcategory_brand_settings || {})[scope] || {}), ...patch }
                }
            }));
        }
    };

    const anyEnabled = formData.organize_by_brands || subs.some(s => subSettings[s]?.organize_by_brands);

    useEffect(() => {
        if (!anyEnabled && !cfg.organize_by_brands) return;
        axios.get(`${API}/admin/brands`, { headers: { Authorization: `Bearer ${token}` } })
            .then(res => setAllBrands(res.data.brands || []))
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [anyEnabled, cfg.organize_by_brands, token]);

    const catBrands = cfg.category_brands || [];
    const brandById = (id) => allBrands.find(b => b.brand_id === id);
    const availableBrands = allBrands.filter(b => !catBrands.some(cb => cb.brand_id === b.brand_id));

    const setCatBrands = (next) => setCfg({ category_brands: next.map((cb, i) => ({ ...cb, position: i })) });

    const addBrand = (brandId) => {
        if (!brandId || catBrands.some(cb => cb.brand_id === brandId)) return;
        setCatBrands([...catBrands, { brand_id: brandId, position: catBrands.length, active: true }]);
        setAddBrandId('');
    };

    const createBrand = async () => {
        const name = newBrandName.trim();
        if (!name) return;
        setCreating(true);
        try {
            const res = await axios.post(`${API}/admin/brands`, { name }, { headers: { Authorization: `Bearer ${token}` } });
            const brand = res.data.brand;
            if (res.data.existing) toast.info(`Марката "${brand.name}" вече съществува — добавена е`);
            else toast.success(`Марката "${brand.name}" е създадена`);
            setAllBrands(prev => prev.some(b => b.brand_id === brand.brand_id) ? prev : [...prev, brand]);
            addBrand(brand.brand_id);
            setNewBrandName('');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Грешка при създаване на марка');
        } finally {
            setCreating(false);
        }
    };

    const move = (index, dir) => {
        const next = [...catBrands];
        const j = index + dir;
        if (j < 0 || j >= next.length) return;
        [next[index], next[j]] = [next[j], next[index]];
        setCatBrands(next);
    };

    const scopeLabel = isCat ? 'категорията' : `подкатегория „${scope}“`;

    return (
        <div className="border-t dark:border-slate-600 pt-4 space-y-4">
            <div className="flex items-center gap-2">
                <Tags className="h-4 w-4 text-amber-500" />
                <Label className="dark:text-slate-300">Организиране на продуктите по марки</Label>
            </div>

            {subs.length > 0 && (
                <div>
                    <Label className="dark:text-slate-300 text-sm">Настройки за</Label>
                    <Select value={scope} onValueChange={setScope}>
                        <SelectTrigger className="mt-1 dark:bg-slate-700 dark:border-slate-600" data-testid="brand-scope-select">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="dark:bg-slate-800">
                            <SelectItem value={CAT_SCOPE}>
                                Цялата категория {formData.organize_by_brands ? '✓' : ''}
                            </SelectItem>
                            {subs.map(s => (
                                <SelectItem key={s} value={s}>
                                    {s} {subSettings[s]?.organize_by_brands ? '✓' : ''}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <p className="text-[11px] text-muted-foreground dark:text-slate-500 mt-1">
                        Можете да включите марки за НЕОГРАНИЧЕН брой подкатегории — изберете подкатегория от менюто, включете превключвателя и подредете марките ѝ, след което изберете следващата. Отметката ✓ показва къде вече е включено.
                    </p>
                    {subs.some(s => subSettings[s]?.organize_by_brands) && (
                        <div className="flex flex-wrap gap-1 mt-2">
                            {subs.filter(s => subSettings[s]?.organize_by_brands).map(s => (
                                <span key={s} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400">
                                    <CheckCircle2 className="h-3 w-3" /> {s}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            )}

            <div className="flex items-center justify-between">
                <div>
                    <Label className="dark:text-slate-300 text-sm">Организирай продуктите по марки</Label>
                    <p className="text-xs text-muted-foreground dark:text-slate-500 mt-1">
                        {isCat
                            ? 'Клиентите ще виждат марките в цялата категория (вкл. всички подкатегории).'
                            : `Активира марките само за ${scopeLabel}. Настройката е с приоритет пред тази на категорията.`}
                    </p>
                </div>
                <Switch
                    checked={cfg.organize_by_brands || false}
                    onCheckedChange={(v) => setCfg({ organize_by_brands: v })}
                    data-testid="category-organize-brands-switch"
                />
            </div>

            {!isCat && formData.organize_by_brands && !cfg.organize_by_brands && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                    Марките са включени за цялата категория, така че тази подкатегория вече ги наследява. Включете горния превключвател само ако искате отделни настройки за нея.
                </p>
            )}

            {cfg.organize_by_brands && (
                <div className="space-y-4 pl-1">
                    <div className="flex items-center justify-between">
                        <div>
                            <Label className="dark:text-slate-300 text-sm">Изисквай марка за всеки продукт</Label>
                            <p className="text-xs text-muted-foreground dark:text-slate-500">Продукт без марка ще се показва като проблемен.</p>
                        </div>
                        <Switch
                            checked={cfg.require_brand || false}
                            onCheckedChange={(v) => setCfg({ require_brand: v })}
                            data-testid="category-require-brand-switch"
                        />
                    </div>

                    <div>
                        <Label className="dark:text-slate-300 text-sm">Подредба на марките</Label>
                        <Select value={cfg.brand_sort || 'manual'} onValueChange={(v) => setCfg({ brand_sort: v })}>
                            <SelectTrigger className="mt-1 dark:bg-slate-700 dark:border-slate-600" data-testid="category-brand-sort-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="dark:bg-slate-800">
                                <SelectItem value="manual">Ръчен ред</SelectItem>
                                <SelectItem value="alpha">Азбучен ред</SelectItem>
                                <SelectItem value="count">По брой продукти</SelectItem>
                                <SelectItem value="popular">Най-популярни</SelectItem>
                                <SelectItem value="newest">Най-нови</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    <div>
                        <Label className="dark:text-slate-300 text-sm">Марки в {scopeLabel}</Label>
                        <div className="mt-2 space-y-2">
                            {catBrands.length === 0 && (
                                <p className="text-xs text-muted-foreground dark:text-slate-500 text-center py-2">
                                    Няма добавени марки. Продукти със зададена марка се показват автоматично.
                                </p>
                            )}
                            {catBrands.map((cb, index) => {
                                const b = brandById(cb.brand_id);
                                return (
                                    <div key={cb.brand_id} className={`flex items-center gap-2 p-2 rounded-lg ${cb.active !== false ? 'bg-slate-100 dark:bg-slate-700' : 'bg-slate-50 dark:bg-slate-700/50 opacity-60'}`} data-testid={`category-brand-row-${cb.brand_id}`}>
                                        <div className="flex flex-col">
                                            <button type="button" onClick={() => move(index, -1)} disabled={index === 0} className="disabled:opacity-30"><ArrowUp className="h-3 w-3 dark:text-slate-400" /></button>
                                            <button type="button" onClick={() => move(index, 1)} disabled={index === catBrands.length - 1} className="disabled:opacity-30"><ArrowDown className="h-3 w-3 dark:text-slate-400" /></button>
                                        </div>
                                        {b?.logo_url && <img src={b.logo_url} alt="" className="h-7 w-7 rounded-full object-cover bg-white" />}
                                        <span className="flex-1 text-sm dark:text-white">
                                            {b?.name || cb.brand_id}
                                            <span className="ml-2 text-xs text-muted-foreground dark:text-slate-400">({b?.product_count ?? 0} продукта)</span>
                                        </span>
                                        <Switch
                                            checked={cb.active !== false}
                                            onCheckedChange={(v) => setCatBrands(catBrands.map((x, i) => i === index ? { ...x, active: v } : x))}
                                            title={cb.active !== false ? 'Активна' : 'Деактивирана'}
                                        />
                                        <Button type="button" size="sm" variant="ghost" onClick={() => setCatBrands(catBrands.filter((_, i) => i !== index))} className="h-7 w-7 p-0" title="Премахни (марката остава в магазина)">
                                            <X className="h-3 w-3 text-red-500" />
                                        </Button>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="flex gap-2 mt-3">
                            <Select value={addBrandId} onValueChange={(v) => { setAddBrandId(v); addBrand(v); }}>
                                <SelectTrigger className="flex-1 dark:bg-slate-700 dark:border-slate-600" data-testid="category-add-brand-select">
                                    <SelectValue placeholder="Добави съществуваща марка..." />
                                </SelectTrigger>
                                <SelectContent className="dark:bg-slate-800">
                                    {availableBrands.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">Няма други марки</div>}
                                    {availableBrands.map(b => (
                                        <SelectItem key={b.brand_id} value={b.brand_id}>{b.name} ({b.product_count ?? 0})</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex gap-2 mt-2">
                            <Input
                                value={newBrandName}
                                onChange={(e) => setNewBrandName(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), createBrand())}
                                placeholder="Създай нова марка..."
                                className="flex-1 dark:bg-slate-700 dark:border-slate-600"
                                data-testid="category-new-brand-input"
                            />
                            <Button type="button" onClick={createBrand} disabled={creating || !newBrandName.trim()} variant="outline" className="dark:border-slate-600 dark:text-white" data-testid="category-create-brand-btn">
                                <Plus className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CategoryBrandsSection;
